import React, { useState } from 'react';

/**
 * Collapsible section component for feedback form
 * @param {Object} props - Component props
 * @param {string} props.title - Section title
 * @param {React.ReactNode} props.children - Section content
 * @param {boolean} [props.visible=true] - Whether section is visible
 * @param {boolean} [props.defaultExpanded=true] - Initial expanded state
 * @param {string} [props.icon] - Optional icon name
 * @returns {JSX.Element|null} FeedbackSection component or null if not visible
 */
const FeedbackSection = ({
  title,
  children,
  visible = true,
  defaultExpanded = true,
  icon
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  if (!visible) {
    return null;
  }

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleExpanded();
    }
  };

  const renderIcon = () => {
    const icons = {
      lectures: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </svg>
      ),
      video: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="5 3 19 12 5 21 5 3" />
        </svg>
      ),
      announcements: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
      ),
      calendar: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      ),
      publications: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </svg>
      ),
      general: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      )
    };
    return icons[icon] || icons.general;
  };

  return (
    <div className={`feedback-section ${isExpanded ? 'expanded' : 'collapsed'}`}>
      <div
        className="feedback-section-header"
        onClick={toggleExpanded}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-controls={`section-content-${title.replace(/\s+/g, '-').toLowerCase()}`}
      >
        <div className="feedback-section-title">
          {icon && <span className="feedback-section-icon">{renderIcon()}</span>}
          <h3>{title}</h3>
        </div>
        <span className="feedback-section-toggle" aria-hidden="true">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={isExpanded ? 'rotated' : ''}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </div>
      <div
        id={`section-content-${title.replace(/\s+/g, '-').toLowerCase()}`}
        className="feedback-section-content"
        hidden={!isExpanded}
      >
        {children}
      </div>
    </div>
  );
};

/**
 * Comment textarea with character limit
 * @param {Object} props - Component props
 * @param {string} props.value - Current value
 * @param {Function} props.onChange - Callback when value changes
 * @param {string} [props.placeholder] - Placeholder text
 * @param {number} [props.maxLength=500] - Maximum character limit
 * @param {string} [props.label] - Label text
 * @param {string} [props.name] - Input name for form handling
 * @returns {JSX.Element} CommentTextarea component
 */
export const CommentTextarea = ({
  value = '',
  onChange,
  placeholder = 'Share your thoughts...',
  maxLength = 500,
  label,
  name
}) => {
  const charCount = value.length;
  const isNearLimit = charCount > maxLength * 0.8;
  const isAtLimit = charCount >= maxLength;

  const handleChange = (e) => {
    const newValue = e.target.value;
    if (newValue.length <= maxLength && onChange) {
      onChange(newValue);
    }
  };

  return (
    <div className="feedback-comment-wrapper">
      {label && <label className="feedback-comment-label">{label}</label>}
      <textarea
        className="feedback-comment-textarea"
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        maxLength={maxLength}
        name={name}
        rows={3}
        aria-describedby={name ? `${name}-char-count` : undefined}
      />
      <div
        id={name ? `${name}-char-count` : undefined}
        className={`feedback-char-count ${isNearLimit ? 'warning' : ''} ${isAtLimit ? 'limit' : ''}`}
        aria-live="polite"
      >
        {charCount}/{maxLength}
      </div>
    </div>
  );
};

export default FeedbackSection;
